import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { useIncidents } from 'context/IncidentsContext';
import {
  ANTECEDENT_OPTIONS,
  BEHAVIOR_TYPE_OPTIONS,
  CONSEQUENCE_OPTIONS,
  FUNCTIONS_OF_BEHAVIOR,
  FUNCTION_BY_VALUE,
  LOCATION_OPTIONS,
} from '../../../lib/pbisConstants';

// ---------------------------------------------------------------------------
// IncidentDataSnapshot
//
// Bridges the "log ABC incidents" workflow and the "write a Behavior
// Support Plan" workflow. Historically these two pages lived on separate
// tabs with no connection — a case manager writing a BSP would open a
// blank form and have to remember (or hand-tabulate) what the data
// actually said. That's the exact gap the feedback called out.
//
// This panel sits at the top of the BSP editor + read view and:
//
//   * Rolls up the student's logged behavior_incidents for a chosen
//     window (defaults to the last 60 days — a common review period).
//   * Surfaces the dominant patterns needed for an FBA hypothesis:
//     dominant function, top antecedent, top behavior type,
//     top consequence, top location, peak time-of-day bucket.
//   * Shows plain-English "suggested" values for the four BSP fields
//     that flow directly from the data — target behavior definition,
//     hypothesized function, hypothesis statement, FBA summary, and a
//     baseline sentence for the SMART goal — each with an "Apply to
//     plan" button. Suggestions never silently overwrite: if the
//     matching plan field is already non-empty we require confirmation.
//   * Lists the most recent 3 incidents with the teacher's notes and
//     a deep-link to the full incident table for the student, so the
//     writer can quote specifics without leaving the page.
// ---------------------------------------------------------------------------

const WINDOW_OPTIONS = [
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 6 months' },
  { value: 'all', label: 'All time' },
];

const labelLookup = (options) =>
  Object.fromEntries((options || []).map((o) => [o.value, o.label]));

const LOOKUPS = {
  antecedent: labelLookup(ANTECEDENT_OPTIONS),
  behavior: labelLookup(BEHAVIOR_TYPE_OPTIONS),
  consequence: labelLookup(CONSEQUENCE_OPTIONS),
  location: labelLookup(LOCATION_OPTIONS),
};

const timeOf = (i) => new Date(i?.occurredAt || i?.timestamp || 0);

const timeOfDayBucket = (hour) => {
  if (hour < 8) return 'before 8 AM';
  if (hour < 10) return 'between 8–10 AM';
  if (hour < 12) return 'between 10 AM–12 PM';
  if (hour < 14) return 'between 12–2 PM';
  if (hour < 16) return 'between 2–4 PM';
  return 'after 4 PM';
};

// Given the enum value and its option list, return the friendly label
// (or a humanized version of the value as a last-resort fallback).
const friendly = (kind, value) => {
  if (!value) return '';
  return LOOKUPS[kind]?.[value] || String(value).replace(/-/g, ' ');
};

const topEntry = (rows, key) => {
  if (!rows?.length) return null;
  const counts = {};
  for (const r of rows) {
    const v = r?.[key];
    if (!v) continue;
    counts[v] = (counts[v] || 0) + 1;
  }
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (!sorted.length) return null;
  const [value, count] = sorted[0];
  return { value, count, share: count / rows.length };
};

const formatShare = (share) => `${Math.round(share * 100)}%`;

const buildSuggestions = (rows, studentFirstName) => {
  if (!rows?.length) {
    return {
      dominantFunction: null,
      topAntecedent: null,
      topBehavior: null,
      topConsequence: null,
      topLocation: null,
      peakHour: null,
      avgIntensity: null,
      avgDurationSeconds: null,
      followUpCount: 0,
      perWeek: 0,
      targetBehaviorSuggestion: '',
      hypothesizedFunctionSuggestion: '',
      hypothesisStatementSuggestion: '',
      fbaSummarySuggestion: '',
      goalBaselineSuggestion: '',
    };
  }

  const dominantFunction = topEntry(rows, 'hypothesizedFunction');
  const topAntecedent = topEntry(rows, 'antecedent');
  const topBehavior = topEntry(rows, 'behaviorType');
  const topConsequence = topEntry(rows, 'consequence');
  const topLocation = topEntry(rows, 'location');

  // Peak hour, rendered as a friendly window instead of a raw integer.
  const hourCounts = {};
  for (const r of rows) {
    const hour = timeOf(r).getHours();
    hourCounts[hour] = (hourCounts[hour] || 0) + 1;
  }
  const peakHourEntry = Object.entries(hourCounts).sort((a, b) => b[1] - a[1])[0];
  const peakHour = peakHourEntry
    ? {
        hour: Number(peakHourEntry[0]),
        count: peakHourEntry[1],
        bucket: timeOfDayBucket(Number(peakHourEntry[0])),
      }
    : null;

  // Intensity + duration averages. Only include incidents that actually
  // recorded the field so a blank field doesn't drag the average toward 0.
  const intensityRows = rows.filter((r) => Number.isFinite(Number(r?.intensity)) && Number(r.intensity) > 0);
  const avgIntensity = intensityRows.length
    ? intensityRows.reduce((s, r) => s + Number(r.intensity), 0) / intensityRows.length
    : null;

  const durationRows = rows.filter((r) => r?.durationSeconds != null);
  const avgDurationSeconds = durationRows.length
    ? durationRows.reduce((s, r) => s + Number(r.durationSeconds || 0), 0) / durationRows.length
    : null;

  const followUpCount = rows.filter((r) => r?.followUpRequired).length;

  // Approximate per-week rate over the observed span. Use the actual
  // range between first and last incident when we can — a two-week span
  // with 6 incidents is ~3/week, not "6 in the whole window".
  const timestamps = rows.map(timeOf).map((d) => d.getTime()).sort((a, b) => a - b);
  const spanMs = timestamps.length > 1 ? timestamps[timestamps.length - 1] - timestamps[0] : 0;
  const spanWeeks = Math.max(1, spanMs / (7 * 24 * 60 * 60 * 1000));
  const perWeek = spanMs > 0 ? rows.length / spanWeeks : rows.length;

  // ---- Suggested prose for the plan fields ---------------------------------
  const behaviorLabel = topBehavior
    ? friendly('behavior', topBehavior.value).toLowerCase()
    : '';
  const antecedentLabel = topAntecedent
    ? friendly('antecedent', topAntecedent.value).toLowerCase()
    : '';
  const consequenceLabel = topConsequence
    ? friendly('consequence', topConsequence.value).toLowerCase()
    : '';
  const locationLabel = topLocation
    ? friendly('location', topLocation.value).toLowerCase()
    : '';
  const fnMeta = dominantFunction ? FUNCTION_BY_VALUE[dominantFunction.value] : null;
  const fnLabel = fnMeta ? fnMeta.label.toLowerCase() : '';
  const fnPhrase =
    dominantFunction?.value === 'escape'
      ? 'in order to escape or avoid a non-preferred task or demand'
      : dominantFunction?.value === 'attention'
        ? 'in order to gain adult or peer attention'
        : dominantFunction?.value === 'tangible'
          ? 'in order to gain access to a preferred item or activity'
          : dominantFunction?.value === 'sensory'
            ? 'in order to meet an internal sensory need'
            : dominantFunction?.value === 'automatic'
              ? '(the behavior appears self-reinforcing; no external maintainer identified)'
              : dominantFunction?.value === 'unknown'
                ? '(the function is not yet clear — additional ABC data is needed)'
                : '';

  const targetBehaviorSuggestion = topBehavior
    ? `${studentFirstName || 'Student'} engages in ${behaviorLabel}` +
      (locationLabel ? `, most often observed in the ${locationLabel},` : ',') +
      ` at a rate of approximately ${perWeek.toFixed(1)} incident(s) per week` +
      (avgIntensity ? ` with an average intensity of ${avgIntensity.toFixed(1)} / 5` : '') +
      (avgDurationSeconds
        ? ` and an average duration of ${Math.round(avgDurationSeconds / 60)} minute(s)`
        : '') +
      '. Replace this sentence with a fully observable + measurable definition your team can score consistently.'
    : '';

  const hypothesizedFunctionSuggestion = dominantFunction?.value || '';

  const hypothesisStatementSuggestion =
    topAntecedent && topBehavior && dominantFunction
      ? `When ${antecedentLabel} occurs, ${studentFirstName || 'the student'} engages in ${behaviorLabel} ${fnPhrase}.`
      : '';

  const fbaLines = [];
  if (rows.length) {
    fbaLines.push(
      `Based on ${rows.length} incident${rows.length === 1 ? '' : 's'} logged for ${studentFirstName || 'this student'}:`,
    );
  }
  if (topBehavior) {
    fbaLines.push(
      `• Most frequent behavior: ${friendly('behavior', topBehavior.value)} (${topBehavior.count}, ${formatShare(topBehavior.share)}).`,
    );
  }
  if (topAntecedent) {
    fbaLines.push(
      `• Most common antecedent: ${friendly('antecedent', topAntecedent.value)} (${formatShare(topAntecedent.share)} of incidents).`,
    );
  }
  if (topConsequence) {
    fbaLines.push(
      `• Most common staff/peer response afterward: ${friendly('consequence', topConsequence.value)} (${formatShare(topConsequence.share)}).`,
    );
  }
  if (topLocation) {
    fbaLines.push(
      `• Most common setting: ${friendly('location', topLocation.value)} (${formatShare(topLocation.share)}).`,
    );
  }
  if (peakHour) {
    fbaLines.push(`• Peak time-of-day: ${peakHour.bucket}.`);
  }
  if (dominantFunction && fnMeta) {
    fbaLines.push(
      `• Dominant hypothesized function: ${fnMeta.label} (${formatShare(dominantFunction.share)} of incidents).`,
    );
  }
  if (avgIntensity) {
    fbaLines.push(`• Average recorded intensity: ${avgIntensity.toFixed(1)} / 5.`);
  }
  if (avgDurationSeconds) {
    fbaLines.push(`• Average recorded duration: ${Math.round(avgDurationSeconds / 60)} minute(s).`);
  }
  const fbaSummarySuggestion = fbaLines.join('\n');

  const goalBaselineSuggestion = rows.length
    ? `Currently ~${perWeek.toFixed(1)} incident(s) per week` +
      (avgIntensity ? `, average intensity ${avgIntensity.toFixed(1)} / 5` : '') +
      (avgDurationSeconds
        ? `, average duration ${Math.round(avgDurationSeconds / 60)} minute(s)`
        : '') +
      '.'
    : '';

  return {
    dominantFunction,
    topAntecedent,
    topBehavior,
    topConsequence,
    topLocation,
    peakHour,
    avgIntensity,
    avgDurationSeconds,
    followUpCount,
    perWeek,
    targetBehaviorSuggestion,
    hypothesizedFunctionSuggestion,
    hypothesisStatementSuggestion,
    fbaSummarySuggestion,
    goalBaselineSuggestion,
  };
};

const IncidentDataSnapshot = ({
  studentId,
  studentName,
  plan,
  onApplySuggestion,
  mode = 'edit',
}) => {
  const [windowDays, setWindowDays] = useState('60');
  const [expanded, setExpanded] = useState(true);
  const [showAllRecent, setShowAllRecent] = useState(false);

  const { incidentsForStudent } = useIncidents();

  const allIncidents = useMemo(
    () => (studentId ? incidentsForStudent(studentId) : []),
    [studentId, incidentsForStudent],
  );

  const filteredIncidents = useMemo(() => {
    if (!allIncidents?.length) return [];
    if (windowDays === 'all') return allIncidents;
    const cutoff = Date.now() - Number(windowDays) * 24 * 60 * 60 * 1000;
    return allIncidents.filter((r) => timeOf(r).getTime() >= cutoff);
  }, [allIncidents, windowDays]);

  const studentFirstName = (studentName || '').split(' ')[0] || 'Student';

  const suggestions = useMemo(
    () => buildSuggestions(filteredIncidents, studentFirstName),
    [filteredIncidents, studentFirstName],
  );

  // Recent incidents — always drawn from the full list (not the window
  // filter) so the writer can still reach a note that predates the window.
  const recentIncidents = useMemo(() => {
    return [...(allIncidents || [])]
      .sort((a, b) => timeOf(b) - timeOf(a))
      .slice(0, showAllRecent ? 10 : 3);
  }, [allIncidents, showAllRecent]);

  const totalCount = filteredIncidents.length;
  const totalAllTime = allIncidents.length;
  const readOnly = mode === 'read';

  // Warn early: FBA best practice is a minimum ~5 incidents before drawing
  // a hypothesis. We don't block anything — the plan is the teacher's
  // decision — we just call it out so it's not silently under-supported.
  const dataIsThin = totalCount > 0 && totalCount < 5;
  const dataIsEmpty = totalCount === 0;

  const canApply = typeof onApplySuggestion === 'function' && !readOnly;

  const requestApply = (field, suggestedValue) => {
    if (!canApply || !suggestedValue) return;
    const existing = (plan?.[field] ?? '').toString().trim();
    if (existing.length > 0) {
      const confirmed = window.confirm(
        `This field already has content. Replace it with the suggestion?\n\nCurrent:\n${existing}\n\nSuggested:\n${suggestedValue}`,
      );
      if (!confirmed) return;
    }
    onApplySuggestion(field, suggestedValue);
  };

  const incidentLogHref = studentId
    ? `/behavioral-incident-logging`
    : '/behavioral-incident-logging';

  return (
    <section className="bg-card border border-border rounded-lg overflow-hidden">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between p-6 border-b border-border bg-primary/5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Database" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-card-foreground">
              What the incident data says
            </h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Rolled up from the behavior incidents you've logged for{' '}
              <span className="font-medium">{studentName || 'this student'}</span>. Use
              this as the evidence base for the plan below — click{' '}
              <span className="font-medium">Apply to plan</span> on any suggestion to
              copy it into the matching field.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="min-w-40">
            <Select
              options={WINDOW_OPTIONS}
              value={windowDays}
              onChange={setWindowDays}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
            {expanded ? 'Hide' : 'Show'}
          </Button>
        </div>
      </header>

      {expanded && (
        <div className="p-6 space-y-6">
          {dataIsEmpty ? (
            <div className="p-4 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 text-amber-900">
              <div className="flex items-start gap-3">
                <Icon
                  name="AlertTriangle"
                  size={20}
                  className="text-amber-600 mt-0.5 flex-shrink-0"
                />
                <div className="flex-1">
                  <p className="font-medium">
                    No incidents logged in this window
                    {totalAllTime > 0 && (
                      <>
                        {' '}
                        (there are {totalAllTime} on file overall — try{' '}
                        <button
                          type="button"
                          onClick={() => setWindowDays('all')}
                          className="underline font-semibold"
                        >
                          All time
                        </button>
                        )
                      </>
                    )}
                    .
                  </p>
                  <p className="text-sm mt-1">
                    A behavior plan without underlying ABC data is a guess. Log a
                    handful of incidents first so the plan reflects real patterns,
                    not memory.
                  </p>
                  <div className="mt-3">
                    <Link to={incidentLogHref}>
                      <Button type="button" size="sm" variant="outline">
                        <Icon name="Plus" size={14} />
                        Log an incident
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {dataIsThin && (
                <div className="p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm flex items-start gap-2">
                  <Icon
                    name="AlertTriangle"
                    size={16}
                    className="text-amber-600 mt-0.5 flex-shrink-0"
                  />
                  <span>
                    Only {totalCount} incident{totalCount === 1 ? '' : 's'} in this
                    window. FBA guidance is to have at least 5 before finalizing a
                    hypothesis — log a few more or widen the window.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricCard
                  icon="Calendar"
                  color="text-primary"
                  label="Incidents in window"
                  value={totalCount}
                  hint={
                    windowDays === 'all'
                      ? 'All incidents on file'
                      : `Last ${windowDays} days`
                  }
                />
                <MetricCard
                  icon="Repeat"
                  color="text-secondary"
                  label="Approx. per week"
                  value={suggestions.perWeek.toFixed(1)}
                  hint="Over the observed span"
                />
                <MetricCard
                  icon="Gauge"
                  color="text-warning"
                  label="Avg. intensity"
                  value={
                    suggestions.avgIntensity
                      ? suggestions.avgIntensity.toFixed(1)
                      : '—'
                  }
                  hint="Scale 1–5"
                />
                <MetricCard
                  icon="Flag"
                  color="text-destructive"
                  label="Need follow-up"
                  value={suggestions.followUpCount}
                  hint="Team review flagged"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <PatternRow
                  icon="Brain"
                  color="text-primary"
                  label="Dominant hypothesized function"
                  value={
                    suggestions.dominantFunction
                      ? FUNCTION_BY_VALUE[suggestions.dominantFunction.value]?.label ||
                        suggestions.dominantFunction.value
                      : '— not enough data'
                  }
                  share={suggestions.dominantFunction?.share}
                />
                <PatternRow
                  icon="AlertTriangle"
                  color="text-warning"
                  label="Most common antecedent"
                  value={
                    suggestions.topAntecedent
                      ? friendly('antecedent', suggestions.topAntecedent.value)
                      : '—'
                  }
                  share={suggestions.topAntecedent?.share}
                />
                <PatternRow
                  icon="Activity"
                  color="text-destructive"
                  label="Most frequent behavior"
                  value={
                    suggestions.topBehavior
                      ? friendly('behavior', suggestions.topBehavior.value)
                      : '—'
                  }
                  share={suggestions.topBehavior?.share}
                />
                <PatternRow
                  icon="MessageCircle"
                  color="text-secondary"
                  label="Most common response afterward"
                  value={
                    suggestions.topConsequence
                      ? friendly('consequence', suggestions.topConsequence.value)
                      : '—'
                  }
                  share={suggestions.topConsequence?.share}
                />
                <PatternRow
                  icon="MapPin"
                  color="text-primary"
                  label="Most common location"
                  value={
                    suggestions.topLocation
                      ? friendly('location', suggestions.topLocation.value)
                      : '—'
                  }
                  share={suggestions.topLocation?.share}
                />
                <PatternRow
                  icon="Clock"
                  color="text-secondary"
                  label="Peak time of day"
                  value={suggestions.peakHour ? suggestions.peakHour.bucket : '—'}
                />
              </div>

              {canApply && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Wand2" size={16} className="text-primary" />
                    <h3 className="text-sm font-semibold text-card-foreground">
                      Suggested prefills for the plan
                    </h3>
                    <span className="text-xs text-muted-foreground">
                      · Review before applying. Each button copies the text into the
                      matching field so you can edit it.
                    </span>
                  </div>
                  <div className="space-y-3">
                    <SuggestionRow
                      title="Target behavior (starter definition)"
                      helper="Rewrite until the definition is fully observable + measurable — this starter just injects your rate + intensity + typical setting."
                      suggestion={suggestions.targetBehaviorSuggestion}
                      onApply={() =>
                        requestApply(
                          'targetBehaviorDefinition',
                          suggestions.targetBehaviorSuggestion,
                        )
                      }
                      currentValue={plan?.targetBehaviorDefinition}
                    />
                    <SuggestionRow
                      title="Hypothesis statement (When → student → in order to)"
                      helper='FBA standard "When [antecedent], the student engages in [behavior] in order to [function]" — pulled from the top antecedent, top behavior, and dominant function above.'
                      suggestion={suggestions.hypothesisStatementSuggestion}
                      onApply={() =>
                        requestApply(
                          'hypothesisStatement',
                          suggestions.hypothesisStatementSuggestion,
                        )
                      }
                      currentValue={plan?.hypothesisStatement}
                    />
                    <SuggestionRow
                      title="FBA summary (multi-line data rollup)"
                      helper="Every rollup on this panel expressed as a paragraph you can drop into the plan and edit."
                      suggestion={suggestions.fbaSummarySuggestion}
                      onApply={() =>
                        requestApply('fbaSummary', suggestions.fbaSummarySuggestion)
                      }
                      currentValue={plan?.fbaSummary}
                    />
                    <SuggestionRow
                      title="Hypothesized function"
                      helper="The function that shows up most in your logged incidents. Applies the enum value the plan's dropdown expects."
                      suggestion={
                        suggestions.hypothesizedFunctionSuggestion
                          ? FUNCTION_BY_VALUE[
                              suggestions.hypothesizedFunctionSuggestion
                            ]?.label ||
                            suggestions.hypothesizedFunctionSuggestion
                          : ''
                      }
                      onApply={() =>
                        requestApply(
                          'hypothesizedFunction',
                          suggestions.hypothesizedFunctionSuggestion,
                        )
                      }
                      currentValue={
                        plan?.hypothesizedFunction
                          ? FUNCTION_BY_VALUE[plan.hypothesizedFunction]?.label ||
                            plan.hypothesizedFunction
                          : ''
                      }
                    />
                    <SuggestionRow
                      title="SMART goal — baseline"
                      helper="One-line snapshot of current rate / intensity / duration you can drop into the Baseline field of the SMART goal."
                      suggestion={suggestions.goalBaselineSuggestion}
                      onApply={() =>
                        requestApply(
                          'goalBaseline',
                          suggestions.goalBaselineSuggestion,
                        )
                      }
                      currentValue={plan?.goalBaseline}
                    />
                  </div>
                </div>
              )}

              {recentIncidents.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon name="ListChecks" size={16} className="text-primary" />
                      <h3 className="text-sm font-semibold text-card-foreground">
                        Most recent incidents (with notes)
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      {allIncidents.length > 3 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowAllRecent((v) => !v)}
                        >
                          {showAllRecent ? 'Show fewer' : `Show up to 10`}
                        </Button>
                      )}
                      <Link to={incidentLogHref}>
                        <Button type="button" variant="outline" size="sm">
                          <Icon name="Table" size={14} />
                          Open full incident log
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {recentIncidents.map((incident) => (
                      <RecentIncident key={incident.id} incident={incident} />
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};

const MetricCard = ({ icon, color, label, value, hint }) => (
  <div className="p-3 rounded-lg border border-border bg-background">
    <div className="flex items-center gap-2 mb-1">
      <Icon name={icon} size={14} className={color} />
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
    </div>
    <p className="text-xl font-bold text-card-foreground">{value}</p>
    {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
  </div>
);

const PatternRow = ({ icon, color, label, value, share }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/20">
    <Icon name={icon} size={16} className={`${color} mt-0.5 flex-shrink-0`} />
    <div className="flex-1 min-w-0">
      <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
        {label}
      </p>
      <p className="text-sm font-semibold text-card-foreground mt-0.5">
        {value}
        {typeof share === 'number' && (
          <span className="ml-1.5 text-xs font-normal text-muted-foreground">
            ({Math.round(share * 100)}% of incidents)
          </span>
        )}
      </p>
    </div>
  </div>
);

const SuggestionRow = ({ title, helper, suggestion, onApply, currentValue }) => {
  const hasSuggestion = Boolean(suggestion && String(suggestion).trim().length > 0);
  const trimmedCurrent = (currentValue || '').toString().trim();
  const alreadyMatches = hasSuggestion && trimmedCurrent === suggestion.trim();

  return (
    <div className="p-4 rounded-lg border border-border bg-background">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-card-foreground">{title}</p>
          {helper && (
            <p className="text-xs text-muted-foreground mt-0.5">{helper}</p>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          variant={alreadyMatches ? 'ghost' : 'outline'}
          onClick={onApply}
          disabled={!hasSuggestion || alreadyMatches}
        >
          <Icon
            name={alreadyMatches ? 'Check' : 'ArrowDownToLine'}
            size={14}
          />
          {alreadyMatches
            ? 'Already applied'
            : trimmedCurrent
              ? 'Replace with suggestion'
              : 'Apply to plan'}
        </Button>
      </div>
      {hasSuggestion ? (
        <pre className="text-xs whitespace-pre-wrap font-sans text-card-foreground bg-muted/30 rounded-md p-3 border border-border">
          {suggestion}
        </pre>
      ) : (
        <p className="text-xs italic text-muted-foreground">
          Not enough data yet to suggest a value for this field.
        </p>
      )}
    </div>
  );
};

const RecentIncident = ({ incident }) => {
  const at = timeOf(incident);
  const dateLabel = at.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeLabel = at.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const fn = incident?.hypothesizedFunction
    ? FUNCTION_BY_VALUE[incident.hypothesizedFunction]
    : null;
  const notes = (incident?.notes || '').trim();
  const description = (incident?.behaviorDescription || '').trim();

  return (
    <li className="p-3 rounded-lg border border-border bg-background">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-1">
        <span className="font-medium text-card-foreground">
          {dateLabel} · {timeLabel}
        </span>
        {incident?.behaviorType && (
          <span className="capitalize">
            · {friendly('behavior', incident.behaviorType)}
          </span>
        )}
        {incident?.location && (
          <span className="capitalize">
            · {friendly('location', incident.location)}
          </span>
        )}
        {fn && (
          <span
            className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-medium border"
            style={{
              borderColor: fn.color,
              color: fn.color,
              backgroundColor: `${fn.color}14`,
            }}
          >
            {fn.label}
          </span>
        )}
      </div>
      {description && (
        <p className="text-sm text-card-foreground whitespace-pre-wrap">{description}</p>
      )}
      {notes && (
        <p className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">
          <span className="font-medium">Notes: </span>
          {notes}
        </p>
      )}
    </li>
  );
};

export default IncidentDataSnapshot;
